import { useEffect, useRef, useState } from 'react';
import { useUpdateServiceItem, type ServiceItemInput } from '../lib/serviceMutations';
import type { ServiceItem } from '../lib/serviceTypes';
import { cn, formatDateShort, toInputDate } from '../lib/utils';

/**
 * Click-to-edit cells, because Sara asked for the board to "act like excel as
 * well, where we can edit cells".
 *
 * Rules that apply to every variant here:
 *
 *  - Click to edit, Enter or blur to save, Escape to cancel. No save button and
 *    no edit mode toggle. A person editing a spreadsheet does not click Edit
 *    first, and adding a step here would be felt on every one of hundreds of
 *    daily edits.
 *
 *  - Nothing is written when the value did not change. Sara tabbing through a
 *    row to read it should not stamp updated_by on six fields and fill the
 *    activity log with edits nobody made.
 *
 *  - A failed save reverts the cell and shows the error in place. Silently
 *    keeping the typed value on screen after the write failed is the worst
 *    outcome: she believes it saved, closes the tab, and it is gone.
 *
 *  - Escape cancels without saving even mid-blur, which is why the escape flag
 *    is a ref rather than state. A state update would not land before the blur
 *    handler reads it.
 */

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function useCellSave(item: ServiceItem) {
    const update = useUpdateServiceItem();
    const [state, setState] = useState<SaveState>('idle');
    const [error, setError] = useState<string | null>(null);

    async function save(input: ServiceItemInput): Promise<boolean> {
        setState('saving');
        setError(null);
        try {
            await update.mutateAsync({ id: item.id, input, previous: item });
            setState('saved');
            setTimeout(() => setState('idle'), 900);
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed');
            setState('error');
            return false;
        }
    }

    return { save, state, error, clearError: () => { setError(null); setState('idle'); } };
}

const cellBase =
    'w-full text-left rounded px-1 -mx-1 transition-colors cursor-text ' +
    'hover:bg-signal-500/10 focus:outline-none focus:ring-1 focus:ring-signal-500';

const inputBase =
    'w-full bg-white dark:bg-graphite-900 border border-signal-500 rounded px-1 py-0.5 ' +
    'text-sm text-graphite-900 dark:text-graphite-100 focus:outline-none';

function StateDot({ state }: { state: SaveState }) {
    if (state === 'idle') return null;
    return (
        <span
            className={cn(
                'ml-1 inline-block w-1.5 h-1.5 rounded-full align-middle',
                state === 'saving' && 'bg-amber-500 animate-pulse',
                state === 'saved' && 'bg-done-500',
                state === 'error' && 'bg-red-500',
            )}
        />
    );
}

function ErrorLine({ error, onDismiss }: { error: string | null; onDismiss: () => void }) {
    if (!error) return null;
    return (
        <button
            type="button"
            onClick={onDismiss}
            className="mt-0.5 block text-left text-[10px] leading-tight text-red-600 dark:text-red-400"
            title="Click to dismiss"
        >
            {error}
        </button>
    );
}

// -----------------------------------------------------------------------------

export function EditableText({
    item,
    field,
    value,
    placeholder = '\u2014',
    mono,
    className,
    required,
    disabled,
}: {
    item: ServiceItem;
    field: keyof ServiceItemInput;
    value: string | null;
    placeholder?: string;
    mono?: boolean;
    /**
     * Typography and colour for the read-only view. Passed in rather than
     * inherited: the view element is a <button>, and relying on a colour
     * cascading down into it silently produced unreadable grey text once the
     * wrapping div's text classes were removed.
     */
    className?: string;
    /** True for NOT NULL columns. An emptied cell reverts instead of writing null. */
    required?: boolean;
    disabled?: boolean;
}) {
    const { save, state, error, clearError } = useCellSave(item);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const escaped = useRef(false);
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (editing) ref.current?.select();
    }, [editing]);

    async function commit() {
        setEditing(false);
        if (escaped.current) {
            escaped.current = false;
            setDraft(value ?? '');
            return;
        }
        const next = draft.trim() === '' ? null : draft.trim();
        if (next === null && required) { setDraft(value ?? ''); return; }
        if (next === (value ?? null)) return;
        const ok = await save({ [field]: next } as ServiceItemInput);
        if (!ok) setDraft(value ?? '');
    }

    if (disabled) {
        return <span className={cn(className ?? 'text-sm', mono && 'font-mono tabular')}>{value || placeholder}</span>;
    }

    return (
        <div className="min-w-0">
            {editing ? (
                <input
                    ref={ref}
                    className={cn(inputBase, mono && 'font-mono tabular')}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                        if (e.key === 'Escape') { escaped.current = true; (e.target as HTMLInputElement).blur(); }
                    }}
                />
            ) : (
                <button
                    type="button"
                    className={cn(cellBase, 'truncate block', className ?? 'text-sm')}
                    onClick={() => { setDraft(value ?? ''); setEditing(true); }}
                    title={value ?? ''}
                >
                    {value || <span className="text-graphite-500">{placeholder}</span>}
                    <StateDot state={state} />
                </button>
            )}
            <ErrorLine error={error} onDismiss={clearError} />
        </div>
    );
}

/**
 * A plain date cell.
 *
 * Requested Date rendered as read-only text until now, and the add form stamped
 * it with today. Kristine, 3 Sep: "Can we also change the requested date to
 * something we can fill in. We sometimes receive emails and when we create that
 * item it needs to be the date of the email."
 *
 * Kept separate from EditableSchedule, which carries a state and a free-text
 * note alongside its date and would be the wrong shape here. The input holds
 * raw yyyy-MM-dd because that is the only thing <input type="date"> accepts,
 * and the read view formats to MM/DD/YY to match the rest of the board.
 */
export function EditableDate({
    item,
    field,
    value,
    placeholder = '\u2014',
    className,
    disabled,
}: {
    item: ServiceItem;
    field: keyof ServiceItemInput;
    value: string | null;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}) {
    const { save, state, error, clearError } = useCellSave(item);
    const [editing, setEditing] = useState(false);

    async function commit(next: string) {
        setEditing(false);
        const v = next || null;
        if (v === (value ?? null)) return;
        await save({ [field]: v } as ServiceItemInput);
    }

    if (disabled) {
        return (
            <span className={cn(className ?? 'text-sm', 'font-mono tabular')}>
                {formatDateShort(value) || placeholder}
            </span>
        );
    }

    return (
        <div className="min-w-0">
            {editing ? (
                <input
                    type="date"
                    autoFocus
                    className={cn(inputBase, 'font-mono tabular')}
                    defaultValue={toInputDate(value)}
                    onBlur={(e) => commit(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                        if (e.key === 'Escape') setEditing(false);
                    }}
                />
            ) : (
                <button
                    type="button"
                    className={cn(cellBase, 'truncate block font-mono tabular', className ?? 'text-sm')}
                    onClick={() => setEditing(true)}
                    title={value ? 'Click to change the date' : 'Click to set a date'}
                >
                    {formatDateShort(value) || <span className="text-graphite-500">{placeholder}</span>}
                    <StateDot state={state} />
                </button>
            )}
            <ErrorLine error={error} onDismiss={clearError} />
        </div>
    );
}

export function EditableTextarea({
    item,
    field,
    value,
    placeholder = '\u2014',
    clamp = 2,
    small,
    className,
    required,
    disabled,
}: {
    item: ServiceItem;
    field: keyof ServiceItemInput;
    value: string | null;
    placeholder?: string;
    clamp?: number;
    small?: boolean;
    className?: string;
    /** True for NOT NULL columns. An emptied cell reverts instead of writing null. */
    required?: boolean;
    disabled?: boolean;
}) {
    const { save, state, error, clearError } = useCellSave(item);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const escaped = useRef(false);
    const ref = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (editing) ref.current?.select();
    }, [editing]);

    async function commit() {
        setEditing(false);
        if (escaped.current) {
            escaped.current = false;
            setDraft(value ?? '');
            return;
        }
        const next = draft.trim() === '' ? null : draft.trim();
        if (next === null && required) { setDraft(value ?? ''); return; }
        if (next === (value ?? null)) return;
        const ok = await save({ [field]: next } as ServiceItemInput);
        if (!ok) setDraft(value ?? '');
    }

    if (disabled) {
        return <span className={className ?? (small ? 'text-[11px]' : 'text-sm')}>{value || placeholder}</span>;
    }

    return (
        <div className="min-w-0">
            {editing ? (
                <textarea
                    ref={ref}
                    rows={4}
                    className={cn(inputBase, 'resize-y leading-snug', small && 'text-[11px]')}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        // Enter inserts a newline here rather than saving. These
                        // fields routinely hold multi-line notes, and Sara pastes
                        // into them. Ctrl+Enter and blur both save.
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            (e.target as HTMLTextAreaElement).blur();
                        }
                        if (e.key === 'Escape') { escaped.current = true; (e.target as HTMLTextAreaElement).blur(); }
                    }}
                />
            ) : (
                <button
                    type="button"
                    className={cn(cellBase, 'block', className ?? (small ? 'text-[11px] leading-snug' : 'text-sm'))}
                    onClick={() => { setDraft(value ?? ''); setEditing(true); }}
                    title={value ?? ''}
                >
                    <span className={cn('break-words', clamp === 2 ? 'line-clamp-2' : 'line-clamp-3')}>
                        {value || <span className="text-graphite-500">{placeholder}</span>}
                    </span>
                    <StateDot state={state} />
                </button>
            )}
            <ErrorLine error={error} onDismiss={clearError} />
        </div>
    );
}

export function EditableSelect({
    item,
    field,
    value,
    options,
    placeholder = '\u2014',
    render,
    allowClear = true,
    parse,
    disabled,
}: {
    item: ServiceItem;
    field: keyof ServiceItemInput;
    value: string | null;
    options: { value: string; label: string }[];
    placeholder?: string;
    render?: (label: string | null) => React.ReactNode;
    /**
     * False for NOT NULL columns like priority. Without this the blank option
     * is always offered and choosing it writes null, which the database
     * rejects with an error the person editing a cell cannot act on.
     */
    allowClear?: boolean;
    /**
     * Converts the chosen option before it is written. Every <select> hands back
     * a string, so a numeric column like priority needs this. Without it the
     * write would depend on PostgREST coercing "3" into a smallint, which is
     * not a behaviour worth betting a cell save on.
     */
    parse?: (v: string) => string | number;
    disabled?: boolean;
}) {
    const { save, state, error, clearError } = useCellSave(item);
    const [editing, setEditing] = useState(false);
    const label = options.find((o) => o.value === value)?.label ?? null;

    async function commit(next: string) {
        setEditing(false);
        if (next === '' && !allowClear) return;
        const v = next === '' ? null : next;
        if (v === (value ?? null)) return;
        const out = v === null ? null : parse ? parse(v) : v;
        await save({ [field]: out } as ServiceItemInput);
    }

    if (disabled) return <>{render ? render(label) : label || placeholder}</>;

    return (
        <div className="min-w-0">
            {editing ? (
                <select
                    autoFocus
                    className={cn(inputBase, 'py-1')}
                    value={value ?? ''}
                    onChange={(e) => commit(e.target.value)}
                    onBlur={() => setEditing(false)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
                >
                    {allowClear && <option value="">{placeholder}</option>}
                    {options.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            ) : (
                <button
                    type="button"
                    className={cn(cellBase, 'block truncate cursor-pointer')}
                    onClick={() => setEditing(true)}
                >
                    {render ? render(label) : (
                        <span className="text-sm text-graphite-800 dark:text-graphite-200">
                            {label || <span className="text-graphite-500">{placeholder}</span>}
                        </span>
                    )}
                    <StateDot state={state} />
                </button>
            )}
            <ErrorLine error={error} onDismiss={clearError} />
        </div>
    );
}

export function EditableMoney({
    item,
    value,
    billable,
    className,
    disabled,
}: {
    item: ServiceItem;
    value: number | null;
    billable: boolean;
    className?: string;
    disabled?: boolean;
}) {
    const { save, state, error, clearError } = useCellSave(item);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value != null ? String(value) : '');
    const escaped = useRef(false);
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => { if (editing) ref.current?.select(); }, [editing]);

    async function commit() {
        setEditing(false);
        if (escaped.current) {
            escaped.current = false;
            setDraft(value != null ? String(value) : '');
            return;
        }
        const raw = draft.replace(/[$,\s]/g, '').trim();
        const next = raw === '' ? null : Number(raw);
        if (next != null && !Number.isFinite(next)) {
            setDraft(value != null ? String(value) : '');
            return;
        }
        if (next === (value ?? null)) return;
        // Typing an amount into a non-billable row means it is billable. Making
        // her set a separate flag first would be a second click for something
        // the number already said.
        const patch: ServiceItemInput = { epo_amount: next };
        if (next != null && !billable) patch.billable = true;
        const ok = await save(patch);
        if (!ok) setDraft(value != null ? String(value) : '');
    }

    const display = value != null
        ? `$${value.toFixed(2)}`
        : billable
          ? null
          : '\u2014';

    if (disabled) {
        return <span className={cn('font-mono text-sm tabular', className)}>{display ?? 'Needs price'}</span>;
    }

    return (
        <div className="min-w-0">
            {editing ? (
                <input
                    ref={ref}
                    inputMode="decimal"
                    className={cn(inputBase, 'font-mono tabular')}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                        if (e.key === 'Escape') { escaped.current = true; (e.target as HTMLInputElement).blur(); }
                    }}
                />
            ) : (
                <button
                    type="button"
                    className={cn(cellBase, 'block font-mono text-sm tabular', className)}
                    onClick={() => { setDraft(value != null ? String(value) : ''); setEditing(true); }}
                >
                    {display ?? (
                        <span className="text-amber-600 dark:text-amber-400 text-[11px] uppercase tracking-widest">
                            Needs price
                        </span>
                    )}
                    <StateDot state={state} />
                </button>
            )}
            <ErrorLine error={error} onDismiss={clearError} />
        </div>
    );
}

/**
 * Schedule state and date together, because they are one decision.
 *
 * The DB check constraint requires a date whenever the state is 'scheduled', so
 * editing them as two independent cells would let someone set 'scheduled' with
 * no date and get a raw Postgres error. Picking a date here implies scheduled,
 * and picking any other state clears the date.
 */
export function EditableSchedule({
    item,
    stateOptions,
    tone,
    disabled,
}: {
    item: ServiceItem;
    stateOptions: { value: string; label: string }[];
    tone: string;
    disabled?: boolean;
}) {
    const { save, state: saveState, error, clearError } = useCellSave(item);
    const [editing, setEditing] = useState(false);
    const [hint, setHint] = useState<string | null>(null);

    // MM/DD/YY on the badge only. The date input below stays on the raw ISO
    // value: an <input type="date"> only accepts yyyy-MM-dd and silently shows
    // blank for anything else.
    const badge =
        item.schedule_state === 'scheduled' && item.scheduled_date
            ? formatDateShort(item.scheduled_date)
            : stateOptions.find((o) => o.value === item.schedule_state)?.label ?? item.schedule_state;

    async function setState(next: string) {
        if (next === item.schedule_state) return;
        // 'Scheduled' with no date is a state the DB check constraint rejects.
        // Say so instead of writing it and surfacing a Postgres error, and
        // instead of silently reverting, which reads as the app ignoring her.
        if (next === 'scheduled' && !item.scheduled_date) {
            setHint('Pick a date below to schedule this');
            return;
        }
        setHint(null);
        await save(
            next === 'scheduled'
                ? { schedule_state: 'scheduled' as never }
                : { schedule_state: next as never, scheduled_date: null },
        );
    }

    async function setDate(next: string) {
        const v = next === '' ? null : next;
        if (v === item.scheduled_date) return;
        setHint(null);
        // Clearing the date on a scheduled row drops it back to unscheduled,
        // handled in reconcileSchedule so both fields move together.
        await save(v ? { scheduled_date: v } : { scheduled_date: null });
    }

    if (disabled) {
        return (
            <div className="min-w-0">
                <span className={cn('inline-block font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded', tone)}>
                    {badge}
                </span>
                {item.schedule_note && (
                    <div className="mt-1 text-[11px] leading-snug text-graphite-600 dark:text-graphite-400 break-words line-clamp-3">
                        {item.schedule_note}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="min-w-0">
            {editing ? (
                <div className="flex flex-col gap-1">
                    <select
                        autoFocus
                        className={cn(inputBase, 'py-0.5 text-xs')}
                        value={item.schedule_state}
                        onChange={(e) => setState(e.target.value)}
                    >
                        {stateOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <input
                        type="date"
                        className={cn(inputBase, 'py-0.5 text-xs font-mono')}
                        value={item.scheduled_date ?? ''}
                        onChange={(e) => setDate(e.target.value)}
                    />
                    {hint && (
                        <span className="text-[10px] leading-tight text-amber-600 dark:text-amber-400">
                            {hint}
                        </span>
                    )}
                    <button
                        type="button"
                        className="text-[10px] uppercase tracking-widest text-graphite-500 hover:text-graphite-800 dark:hover:text-graphite-200 text-left"
                        onClick={() => { setHint(null); setEditing(false); }}
                    >
                        Done
                    </button>
                </div>
            ) : (
                <button type="button" className={cn(cellBase, 'block cursor-pointer')} onClick={() => setEditing(true)}>
                    <span className={cn('inline-block font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded', tone)}>
                        {badge}
                    </span>
                    <StateDot state={saveState} />
                    {item.schedule_note && (
                        <span className="mt-1 block text-[11px] leading-snug text-graphite-600 dark:text-graphite-400 break-words line-clamp-3">
                            {item.schedule_note}
                        </span>
                    )}
                </button>
            )}
            <ErrorLine error={error} onDismiss={clearError} />
        </div>
    );
}

export function EditableFlag({
    item,
    field,
    value,
    label,
    onClass,
    disabled,
}: {
    item: ServiceItem;
    field: keyof ServiceItemInput;
    value: boolean;
    label: string;
    onClass: string;
    disabled?: boolean;
}) {
    const { save, state, error, clearError } = useCellSave(item);

    if (disabled) {
        return value ? (
            <span className={cn('font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 rounded', onClass)}>
                {label}
            </span>
        ) : null;
    }

    return (
        <>
            <button
                type="button"
                onClick={() => save({ [field]: !value } as ServiceItemInput)}
                className={cn(
                    'font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 rounded transition-colors',
                    value
                        ? onClass
                        : 'border border-dashed border-graphite-300 dark:border-graphite-700 text-graphite-400 hover:text-graphite-700 dark:hover:text-graphite-300',
                )}
                title={value ? `Remove ${label}` : `Mark ${label}`}
            >
                {label}
                <StateDot state={state} />
            </button>
            <ErrorLine error={error} onDismiss={clearError} />
        </>
    );
}
